import { useCallback, useMemo } from 'react'

import { DatePicker as AntdDatePicker, DatePickerProps, Form } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import omit from 'lodash.omit'
import styled from 'styled-components'
import { TimeZone } from 'types/graphql'

import { DatePicker as GWUIDatePicker } from '../../govwell-ui'
import { Size } from '../../govwell-ui/types'
import { DateFormats, getFirstAllowableDay } from '../../utils/date'

import { useAntdPopupContainer } from './util'
import Wrapper, { FormFieldWrapperProps } from './Wrapper'

export enum DatePickerAllowedDates {
  Future = 'future',
  Past = 'past',
  All = 'all',
}

type Props = FormFieldWrapperProps & {
  allowClear?: boolean
  allowedDates?: DatePickerAllowedDates
  cutoffTime?: Date | string | null
  disableWeekends?: boolean
  isEndDateRequired?: boolean
  isStartDateRequired?: boolean
  onValueChange?: (values: [Dayjs | null, Dayjs | null] | null) => void
  organizationId?: number
  placeholder?: string | [string, string]
  range?: boolean
  shouldExcludeHolidays?: boolean
  showTime?: boolean
  size?: DatePickerProps['size']
  timeZone?: TimeZone | null
}

const { RangePicker } = AntdDatePicker

const StyledAntdDatePicker = styled(AntdDatePicker)<{
  $size: React.ComponentProps<typeof AntdDatePicker>['size']
}>`
  width: 100%;
  height: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '24px'
      case 'middle':
        return '32px'
      default:
        return '40px'
    }
  }};
`
const StyledAntdRangePicker = styled(RangePicker)<{
  $size: React.ComponentProps<typeof AntdDatePicker>['size']
}>`
  width: 100%;
  height: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '24px'
      case 'middle':
        return '32px'
      default:
        return '40px'
    }
  }};
`

export function DatePicker(props: Props) {
  const {
    placeholder = '',
    showTime,
    range,
    allowedDates,
    isStartDateRequired = true,
    isEndDateRequired = true,
    disableWeekends,
    cutoffTime,
    disabled,
    organizationId,
    size = 'middle',
    allowClear = true,
    timeZone,
    onValueChange,
    shouldExcludeHolidays = false,
    ...formFieldWrapperProps
  } = props

  const firstAllowableDay = useMemo(
    () =>
      allowedDates === DatePickerAllowedDates.Future && cutoffTime
        ? getFirstAllowableDay(cutoffTime, timeZone, !!disableWeekends)
        : undefined,
    [allowedDates, cutoffTime, disableWeekends, timeZone]
  )

  const disabledDate = useCallback(
    (current: Dayjs) => {
      if (!allowedDates) {
        return true
      }

      if (allowedDates === DatePickerAllowedDates.All) {
        return false
      }

      const day = dayjs(current).day()
      const isWeekend = day === 0 || day === 6

      if (firstAllowableDay && dayjs(current).isBefore(firstAllowableDay, 'day')) {
        return true
      }

      if (disableWeekends && isWeekend) {
        return true
      }

      if (allowedDates === DatePickerAllowedDates.Future) {
        return dayjs(current) <= dayjs() && !dayjs(current).isSame(dayjs(), 'day')
      } else if (allowedDates === DatePickerAllowedDates.Past) {
        return dayjs(current) > dayjs()
      }

      return false
    },
    [allowedDates, firstAllowableDay, disableWeekends]
  )

  const form = Form.useFormInstance()
  const fieldName = formFieldWrapperProps.fieldName
  Form.useWatch(fieldName, form)
  const handleBlur = useCallback(async () => {
    try {
      await form.validateFields([fieldName])
    } catch {
      // do nothing
    }
  }, [form, fieldName])

  const commonProps = {
    disabledDate,
  }

  const allowEmptyArr: [boolean, boolean] = useMemo(
    () => [!isStartDateRequired, !isEndDateRequired],
    [isStartDateRequired, isEndDateRequired]
  )
  const getPopupContainer = useAntdPopupContainer()

  if (range) {
    return (
      <Wrapper {...formFieldWrapperProps}>
        <StyledAntdRangePicker
          {...commonProps}
          getPopupContainer={getPopupContainer}
          onChange={onValueChange}
          allowEmpty={allowEmptyArr}
          disabled={disabled}
          format={showTime ? DateFormats.NumericalDateTimeConcise : DateFormats.MonthNameDateNoTime}
          size={size}
          $size={size}
          placeholder={placeholder as [string, string]}
          showTime={
            showTime
              ? {
                  format: DateFormats.NumericalDateTimeConcise,
                  use12Hours: true,
                  minuteStep: 1,
                  defaultValue: [dayjs().startOf('day'), dayjs().endOf('day')],
                }
              : false
          }
        />
      </Wrapper>
    )
  }
  const formats = [
    showTime ? DateFormats.MonthNameDateTime : DateFormats.MonthNameDateNoTime,
    'MM/DD/YYYY',
    'MM/DD/YY',
    'M/D/YYYY',
    'M/D/YY',
    'MMMM D, YYYY',
    'MMMM D YYYY',
    'MMM D, YYYY',
    'MMM D YYYY',
    'D MMMM YYYY',
    'D MMM YYYY',
    'YYYY/MM/DD',
    'YYYY-MM-DD',
    'MMDDYYYY',
    'MMDDYY',
  ]

  if (!showTime) {
    const gwuiSize: Size = size === 'small' ? 'sm' : size === 'middle' ? 'md' : 'lg'
    return (
      <Wrapper {...omit(formFieldWrapperProps, 'caption', 'label')} isGovWellUI>
        {({
          errorMessage: errorMessageToDisplay,
          onValueChange: wrapperOnValueChange,
          tooltip,
          value: wrapperValue,
        }) => {
          return (
            <GWUIDatePicker
              {...(formFieldWrapperProps.label
                ? { label: formFieldWrapperProps.label || '' }
                : { ariaLabel: formFieldWrapperProps.ariaLabel || '' })}
              caption={formFieldWrapperProps.caption}
              errorMessage={errorMessageToDisplay}
              getIsDateDisabled={disabledDate}
              isClearable={allowClear}
              isDisabled={disabled}
              isInvalid={!!errorMessageToDisplay}
              isRequired={!!formFieldWrapperProps.required}
              hideRequiredAsterisk={formFieldWrapperProps.hideRequiredAsterisk}
              onBlur={handleBlur}
              onValueChange={wrapperOnValueChange}
              organizationId={organizationId}
              placeholder={Array.isArray(placeholder) ? placeholder[0] : placeholder}
              shouldExcludeHolidays={shouldExcludeHolidays}
              size={gwuiSize}
              timeZone={timeZone}
              tooltip={tooltip}
              value={wrapperValue}
              width={formFieldWrapperProps.width}
            />
          )
        }}
      </Wrapper>
    )
  }

  return (
    <Wrapper {...formFieldWrapperProps}>
      <StyledAntdDatePicker
        getPopupContainer={getPopupContainer}
        placeholder={placeholder as string}
        format={formats}
        disabled={disabled}
        size={size}
        $size={size}
        allowClear={allowClear}
        showTime={
          showTime
            ? {
                format: DateFormats.MonthNameDateTime,
                use12Hours: true,
                minuteStep: 1,
              }
            : false
        }
        {...commonProps}
      />
    </Wrapper>
  )
}

export default DatePicker
